import { createReducer } from '@reduxjs/toolkit';
import { Reducer } from 'redux';

import { countryDefaultCode } from '@/i18n/countries';
import { FilterPreset, FilterPresetDisplay } from '@/types/filter-presets';
import { eventFilterKeys, FilterItem } from '@/types/filters';
import { _window } from '@/utils/ssr-window';

import {
    CalendarFiltersActions,
    calendarFiltersKey,
    CalendarFiltersState,
    filterDeselect,
    filterSelect,
    filtersLoad,
    filtersLoadSuccess,
    filtersReset,
    searchRangeSet,
    searchSortByScoreSet,
    searchStringSet,
} from './calendar-filters.actions';
import { Filters, FiltersStored } from './calendar-filters.types';

const filtersState = (): Filters => ({
    city: [],
    event_type: [],
    language: [],
    price: [],
    countryCode: countryDefaultCode,
});

const initialState: CalendarFiltersState = {
    filtersAvailable: filtersState(),
    filtersSelected: filtersState(),
    filterPreset: null,
    searchArgs: {
        searchString: '',
        ranged: false,
        sortByScore: true,
    },
};

export const calendarFiltersReducer: Reducer<CalendarFiltersState, CalendarFiltersActions> = createReducer(
    initialState,
    (builder) => {
        builder
            .addCase(filtersLoad, (state: CalendarFiltersState) =>
                Object.assign<object, CalendarFiltersState, Partial<CalendarFiltersState>>({}, state, {
                    filtersAvailable: filtersState(),
                }),
            )
            .addCase(filtersLoadSuccess, (state: CalendarFiltersState, action: ReturnType<typeof filtersLoadSuccess>) =>
                Object.assign<
                    object,
                    CalendarFiltersState,
                    Partial<CalendarFiltersState>,
                    Pick<CalendarFiltersState, 'filterPreset'>
                >(
                    {},
                    state,
                    action.payload.filterPreset
                        ? applyPreset(action.payload.filters, action.payload.filterPreset)
                        : restoreStored(action.payload.filters),
                    {
                        filterPreset: action.payload.filterPreset
                            ? deserializePreset(action.payload.filterPreset)
                            : null,
                    },
                ),
            )
            .addCase(filterSelect, (state: CalendarFiltersState, action: ReturnType<typeof filterSelect>) => {
                const statePart = setSelected(state.filtersAvailable, action.payload, true);
                return Object.assign<object, CalendarFiltersState, Partial<CalendarFiltersState>>({}, state, {
                    filtersAvailable: statePart?.filtersAvailable || state.filtersAvailable,
                    filtersSelected: statePart?.filtersSelected || state.filtersSelected,
                });
            })
            .addCase(filterDeselect, (state: CalendarFiltersState, action: ReturnType<typeof filterDeselect>) => {
                const statePart = setSelected(state.filtersAvailable, action.payload, false);
                return Object.assign<object, CalendarFiltersState, Partial<CalendarFiltersState>>({}, state, {
                    filtersAvailable: statePart?.filtersAvailable || state.filtersAvailable,
                    filtersSelected: statePart?.filtersSelected || state.filtersSelected,
                });
            })
            .addCase(filtersReset, (state: CalendarFiltersState) => {
                const available = JSON.parse(
                    JSON.stringify(state.filtersAvailable),
                ) as CalendarFiltersState['filtersAvailable'];
                for (const fKey of eventFilterKeys) {
                    for (const item of available[fKey]) item.isSelected = false;
                }
                const selected = filtersState();
                selected.countryCode = state.filtersSelected.countryCode;
                return Object.assign<object, CalendarFiltersState, Partial<CalendarFiltersState>>({}, state, {
                    filtersAvailable: available,
                    filtersSelected: selected,
                });
            })
            .addCase(searchStringSet, (state: CalendarFiltersState, action: ReturnType<typeof searchStringSet>) =>
                Object.assign<object, CalendarFiltersState, Partial<CalendarFiltersState>>({}, state, {
                    searchArgs: { ...state.searchArgs, searchString: action.payload },
                }),
            )
            .addCase(searchRangeSet, (state: CalendarFiltersState, action: ReturnType<typeof searchRangeSet>) =>
                Object.assign<object, CalendarFiltersState, Partial<CalendarFiltersState>>({}, state, {
                    searchArgs: { ...state.searchArgs, ranged: action.payload },
                }),
            )
            .addCase(
                searchSortByScoreSet,
                (state: CalendarFiltersState, action: ReturnType<typeof searchSortByScoreSet>) =>
                    Object.assign<object, CalendarFiltersState, Partial<CalendarFiltersState>>({}, state, {
                        searchArgs: { ...state.searchArgs, sortByScore: action.payload },
                    }),
            );
    },
);

function setSelected(
    _filtersAvailable: CalendarFiltersState['filtersAvailable'],
    filter: FilterItem,
    isSelected: boolean,
): Pick<CalendarFiltersState, 'filtersAvailable' | 'filtersSelected'> | null {
    const filtersAvailable = JSON.parse(JSON.stringify(_filtersAvailable)) as CalendarFiltersState['filtersAvailable'];
    const itemIx = filtersAvailable[filter.key].findIndex((item) => item.value === filter.value);
    if (itemIx < 0) return null;
    filtersAvailable[filter.key][itemIx].isSelected = isSelected;
    const filtersSelected = filtersState();
    for (const fKey of eventFilterKeys) {
        filtersSelected[fKey] = filtersAvailable[fKey].filter((item) => item.isSelected);
    }
    filtersSelected.countryCode = _filtersAvailable.countryCode;
    return { filtersAvailable: filtersAvailable, filtersSelected: filtersSelected };
}

function restoreStored(
    filtersAvailable: CalendarFiltersState['filtersAvailable'],
): Pick<CalendarFiltersState, 'filtersAvailable' | 'filtersSelected'> {
    const filtersSelected = filtersState();
    const stored = JSON.parse(
        _window?.localStorage?.getItem(`${calendarFiltersKey}_${filtersAvailable.countryCode}`) || '{}',
    ) as Partial<FiltersStored>;
    if (Object.keys(stored).length > 0) {
        for (const fKey of eventFilterKeys) {
            for (const value of stored[fKey] || []) {
                const itemIx = filtersAvailable[fKey].findIndex((item) => item.value === value);
                if (itemIx < 0) continue;
                filtersAvailable[fKey][itemIx].isSelected = true;
                filtersSelected[fKey].push(filtersAvailable[fKey][itemIx]);
            }
        }
    }
    filtersSelected.countryCode = filtersAvailable.countryCode;
    return { filtersAvailable: filtersAvailable, filtersSelected: filtersSelected };
}

function applyPreset(
    _filtersAvailable: CalendarFiltersState['filtersAvailable'],
    preset: FilterPreset,
): Pick<CalendarFiltersState, 'filtersAvailable' | 'filtersSelected'> {
    const filtersAvailable: CalendarFiltersState['filtersAvailable'] = JSON.parse(JSON.stringify(_filtersAvailable));
    const filtersSelected = filtersState();
    for (const fKey of eventFilterKeys) {
        const attribute = preset[fKey];
        if (!attribute) continue;
        if (Array.isArray(attribute)) {
            for (const value of attribute) {
                const itemIx = filtersAvailable[fKey].findIndex((item) => item.value === value);
                if (itemIx < 0) continue;
                filtersAvailable[fKey][itemIx].isSelected = true;
                filtersSelected[fKey].push(filtersAvailable[fKey][itemIx]);
            }
        } else {
            const itemIx = filtersAvailable[fKey].findIndex((item) => item.value === attribute);
            if (itemIx < 0) continue;
            filtersAvailable[fKey][itemIx].isSelected = true;
            filtersSelected[fKey].push(filtersAvailable[fKey][itemIx]);
        }
    }
    filtersSelected.countryCode = filtersAvailable.countryCode;
    return { filtersAvailable: filtersAvailable, filtersSelected: filtersSelected };
}

function deserializePreset(preset: FilterPreset): FilterPreset<FilterPresetDisplay> {
    return Object.assign<object, FilterPreset, Pick<FilterPreset<FilterPresetDisplay>, 'display'>>({}, preset, {
        display: Object.fromEntries(preset.display.split('|').map((prop) => prop.split(':'))),
    });
}
